/**
 * Storage adapter abstraction for localStorage operations.
 * Enables testability by allowing in-memory storage in tests.
 */

/**
 * Interface for storage operations.
 * Mirrors the Web Storage API (localStorage/sessionStorage).
 */
export interface StorageAdapter {
    /**
     * Returns the value associated with the given key, or null if the key does not exist.
     */
    getItem(key: string): string | null

    /**
     * Sets the value for the given key.
     */
    setItem(key: string, value: string): void

    /**
     * Removes the item with the given key.
     */
    removeItem(key: string): void

    /**
     * Removes all items from storage.
     */
    clear(): void
}

/**
 * Browser localStorage adapter.
 * Use this in production code.
 */
export const browserStorage: StorageAdapter = {
    getItem: (key): string | null => {
        if (typeof localStorage === 'undefined') return null
        return localStorage.getItem(key)
    },
    setItem: (key, value) => {
        if (typeof localStorage === 'undefined') return
        localStorage.setItem(key, value)
    },
    removeItem: (key) => {
        if (typeof localStorage === 'undefined') return
        localStorage.removeItem(key)
    },
    clear: () => {
        if (typeof localStorage === 'undefined') return
        localStorage.clear()
    },
}

/**
 * Factory function to create an in-memory storage adapter.
 * Use this in tests to avoid polluting localStorage.
 *
 * @param initialData - Optional initial data to populate the store
 * @returns A StorageAdapter backed by an in-memory Map
 */
export const createMemoryStorage = (
    initialData?: Record<string, string>
): StorageAdapter & {
    /**
     * Returns the current storage contents as a plain object.
     * Useful for test assertions.
     */
    getAll: () => Record<string, string>
    /**
     * Returns the number of items in storage.
     */
    size: () => number
} => {
    const store = new Map<string, string>(initialData ? Object.entries(initialData) : [])

    return {
        getItem: (key) => store.get(key) ?? null,
        setItem: (key, value) => {
            store.set(key, value)
        },
        removeItem: (key) => {
            store.delete(key)
        },
        clear: () => {
            store.clear()
        },
        getAll: () => Object.fromEntries(store),
        size: () => store.size,
    }
}

/**
 * Storage keys used throughout the application.
 * Centralized here to prevent typos and enable easier refactoring.
 */
export const STORAGE_KEYS = {
    ACCESS_TOKEN: 'access_token',
    REFRESH_TOKEN: 'refresh_token',
    USER: 'user',
    OFFLINE_QUEUE: 'offline_queue',
} as const
